import { useEffect, useState, type RefObject } from "react";

/**
 * Hlídá viditelnost DOM elementu přes `IntersectionObserver`. Vrátí `true` když
 * je element alespoň částečně ve viewportu. Slouží pro pause-on-offscreen pattern
 * (chart, který není vidět, nemusí fetchovat ani re-renderovat).
 *
 * Viz docs/plans/CHART-DATA-REFACTOR.md (Fáze 6).
 */
export const useInView = (
  ref: RefObject<HTMLElement | null>,
  options?: IntersectionObserverInit
): boolean => {
  // SSR / starý browser bez IntersectionObserver → předpokládat visible (bezpečnější).
  // Initial true na klientovi je OK — observer pak (async) opraví na skutečnou hodnotu.
  const [inView, setInView] = useState(true);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setInView(entry.isIntersecting);
      },
      { threshold: 0, rootMargin: "100px", ...options }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, options]);

  return inView;
};
